// src/components/CompareChart.jsx
// Overview (3.48.2): today against the same day last week, hour by hour.
// Running totals: today's line (solid, with a soft fill) grows up to "now";
// last week's (dashed grey) runs the whole day, so the gap between them shows
// at a glance why the Today card's "vs last week" changes during the day.
// Sent and Received tabs (only those this person can see). Data:
// GET /portal/overview/compare (backend lib/overview.js compare()).
import { useId, useMemo, useState } from 'react';
import Loader from './Loader';

const W = 400, H = 200, L = 30, R = 10, T = 14, B = 24;  // about life size in a third of the row
const COLORS = { sent: 'var(--c-1e88e5)', received: 'var(--c-00897b)' };
const runningTotal = list => list.reduce((acc, n) => { acc.push((acc[acc.length - 1] || 0) + (n || 0)); return acc; }, [0]);
const sum = list => (list || []).reduce((a, b) => a + (b || 0), 0);
function niceStep(max) {
  for (const s of [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000]) if (max / s <= 4) return s;
  return Math.ceil(max / 4);
}

export default function CompareChart({ data, canOut, canIn }) {
  const [pick, setPick] = useState(null);
  const [hover, setHover] = useState(null);
  const gid = useId().replace(/:/g, '');
  const kind = pick || (canOut ? 'sent' : 'received');
  const view = useMemo(() => {
    if (!data || data.error) return null;
    // 3.62.15: a clock-change day has 23 or 25 hours (N)
    const N = Math.max(data.labels?.length || 24, Math.ceil(data.nowHour || 0));
    const lw = runningTotal(data.lastWeek?.[kind] || []);          // N + 1 points: 0…N h
    const todayHours = data.today?.[kind] || [];
    const td = runningTotal(todayHours);                          // up to the current hour
    const now = Math.max(0, Math.min(N, data.nowHour || 0));
    const soFar = sum(todayHours);
    const byNow = data.lastWeekByNow?.[kind] ?? null;
    const dayTotal = lw[lw.length - 1];
    const top = Math.max(1, dayTotal, soFar);
    const step = niceStep(top);
    const max = Math.ceil(top / step) * step;
    return { N, lw, td, now, soFar, byNow, dayTotal, max, step };
  }, [data, kind]);

  if (!data) return <Loader size="sm" />;
  if (data.error || !view) return <div style={{ color: 'var(--t-90a4ae)', fontSize: 13 }}>Couldn’t load the comparison.</div>;
  const { N, lw, td, now, soFar, byNow, dayTotal, max, step } = view;
  const day = data.weekday || 'last week';
  const x = h => L + (h / N) * (W - L - R);
  const y = v => T + (1 - v / max) * (H - T - B);
  const lwPts = lw.map((v, h) => `${x(h)},${y(v)}`).join(' ');
  // Today: whole hours so far, then the current moment
  const tdPts = [...td.slice(0, Math.floor(now) + 1).map((v, h) => [x(h), y(v)]), [x(now), y(soFar)]];
  const tdLine = tdPts.map(p => p.join(',')).join(' ');
  const tdArea = `${x(0)},${y(0)} ${tdLine} ${x(now)},${y(0)}`;
  const diff = byNow ? Math.round(((soFar - byNow) / byNow) * 100) : null;
  const color = COLORS[kind];
  const lwAt = h => lw[Math.min(lw.length - 1, h + 1)];
  const tdAt = h => (h + 1 <= now ? td[h + 1] : h < now ? soFar : null);
  const ticks = []; for (let v = 0; v <= max; v += step) ticks.push(v);

  return (
    <div className="cmp">
      <style>{CSS}</style>
      {canOut && canIn && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 10 }} data-pills role="tablist" aria-label="Sent or received">
          {[['sent', 'Sent'], ['received', 'Received']].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={kind === k} onClick={() => setPick(k)} className={'cmp-pill' + (kind === k ? ' on' : '')}>{l}</button>
          ))}
        </div>
      )}
      <div className="cmp-stats">
        <div className="cmp-stat now" style={{ '--k': color }}>
          <span>Today so far</span><b>{soFar}</b>
        </div>
        <div className="cmp-stat">
          <span>{day} by now</span><b>{byNow ?? '—'}</b>
          {diff != null && <em className={diff < 0 ? 'down' : diff > 0 ? 'up' : ''}>{diff === 0 ? 'Same pace' : `${diff > 0 ? '↑' : '↓'} ${Math.abs(diff)}% ${diff > 0 ? 'more' : 'fewer'} today`}</em>}
        </div>
        <div className="cmp-stat">
          <span>{day} all day</span><b>{dayTotal}</b>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" className="cmp-svg"
        aria-label={`Running total of faxes ${kind} by hour: today ${soFar} so far, ${day} ${byNow ?? 'unknown'} by this time and ${dayTotal} all day`}
        onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={`fill${gid}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.32 }} />
            <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        {ticks.map(v => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} style={{ stroke: 'var(--c-eceff1)' }} />
            <text x={L - 6} y={y(v) + 3} textAnchor="end" fontSize="10" style={{ fill: 'var(--t-90a4ae)' }}>{v}</text>
          </g>
        ))}
        {[0, 6, 12, 18].map(h => <text key={h} x={x(h)} y={H - 8} fontSize="10" textAnchor={h ? 'middle' : 'start'} style={{ fill: 'var(--t-90a4ae)' }}>{data.labels?.[h] || ''}</text>)}
        <g className="cmp-reveal" key={kind}>
          <polyline points={lwPts} fill="none" strokeWidth="2.2" strokeDasharray="5 4" strokeLinejoin="round" style={{ stroke: 'var(--c-90a4ae)' }} />
          <polygon points={tdArea} fill={`url(#fill${gid})`} className="cmp-area" />
          <polyline points={tdLine} fill="none" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" style={{ stroke: color }} />
        </g>
        <line x1={x(now)} x2={x(now)} y1={T} y2={H - B} strokeDasharray="2 3" style={{ stroke: color, opacity: 0.6 }} />
        <text x={Math.min(x(now) + 4, W - R - 20)} y={T + 9} fontSize="10" fontWeight="700" style={{ fill: color }}>now</text>
        {byNow != null && <circle cx={x(now)} cy={y(byNow)} r="4" strokeWidth="2" style={{ fill: 'var(--c-ffffff)', stroke: 'var(--c-90a4ae)' }} />}
        <circle cx={x(now)} cy={y(soFar)} r="5" className="cmp-dot" style={{ fill: color }} />
        <circle cx={x(now)} cy={y(soFar)} r="5" className="cmp-ping" style={{ fill: 'none', stroke: color }} />
        {Array.from({ length: N }, (_, h) => (
          <rect key={h} x={x(h)} y={T} width={x(1) - x(0)} height={H - T - B} fill="transparent" onMouseEnter={() => setHover(h)} />
        ))}
        {hover != null && (() => {
          const bx = Math.min(Math.max(x(hover + 0.5) - 62, L), W - R - 124);
          const t = tdAt(hover);
          return (
            <g pointerEvents="none">
              <line x1={x(hover + 1)} x2={x(hover + 1)} y1={T} y2={H - B} style={{ stroke: 'var(--c-b0bec5)' }} />
              <rect x={bx} y={T + 14} width="124" height="46" rx="8" style={{ fill: 'var(--c-263238)', opacity: 0.94 }} />
              <text x={bx + 10} y={T + 30} fontSize="10.5" fontWeight="700" style={{ fill: 'var(--t-ffffff)' }}>By {data.labels?.[(hover + 1) % (data.labels?.length || 24)] || ''}</text>
              <text x={bx + 10} y={T + 44} fontSize="10.5" style={{ fill: 'var(--t-ffffff)' }}>Today: {t == null ? '—' : t}</text>
              <text x={bx + 10} y={T + 56} fontSize="10.5" style={{ fill: 'var(--t-ffffff)' }}>{day}: {lwAt(hover)}</text>
            </g>
          );
        })()}
      </svg>
      <div className="cmp-legend">
        <span><i style={{ background: color }} />Today (running total)</span>
        <span><i className="dash" />{day} last week</span>
      </div>
    </div>
  );
}

const CSS = `
.cmp-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-bottom: 8px; }
.cmp-stat { position: relative; overflow: hidden; border-radius: 10px; padding: 8px 10px; background: var(--c-f5faf9); border: 1px solid var(--c-e0f2f1); display: grid; gap: 1px; min-width: 0; }
.cmp-stat span { font-size: 11px; color: var(--t-546e7a); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cmp-stat b { font-size: 20px; line-height: 1.15; color: var(--t-37474f); }
.cmp-stat em { font-style: normal; font-size: 11px; font-weight: 700; color: var(--t-546e7a); }
.cmp-stat em.up { color: var(--t-2e7d32); }
.cmp-stat em.down { color: var(--t-e65100); }
.cmp-stat.now { border-color: transparent; background: linear-gradient(135deg, color-mix(in srgb, var(--k) 18%, var(--c-ffffff)), color-mix(in srgb, var(--k) 4%, var(--c-ffffff))); box-shadow: inset 0 0 0 1.5px var(--k); }
.cmp-stat.now b { color: var(--k); font-size: 22px; }
.cmp-stat.now::after { content: ''; position: absolute; top: 0; bottom: 0; width: 40%; left: -60%; background: linear-gradient(100deg, transparent, rgb(var(--c-ffffff-rgb) / calc(0.55 * var(--shine-k))), transparent); transform: skewX(-18deg); animation: cmpShine 5s ease-in-out 1s infinite; }
.cmp-pill { border: 1.5px solid var(--c-cfd8dc); background: var(--c-ffffff); color: var(--t-546e7a); border-radius: 99px; padding: 3px 12px; font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
.cmp-pill.on { background: var(--c-00897b); border-color: var(--c-00897b); color: var(--t-ffffff); }
.cmp-svg { display: block; overflow: visible; }
.cmp-reveal { animation: cmpReveal 1.1s cubic-bezier(.3,.7,.3,1) both; }
.cmp-area { animation: cmpFade 1.4s ease .3s both; }
.cmp-dot { animation: cmpPop .5s cubic-bezier(.3,1.8,.5,1) .9s both; transform-box: fill-box; transform-origin: center; }
.cmp-ping { animation: cmpPing 2.2s ease-out 1.2s infinite; transform-box: fill-box; transform-origin: center; }
.cmp-legend { display: flex; gap: 16px; flex-wrap: wrap; font-size: 12px; color: var(--t-546e7a); margin-top: 4px; }
.cmp-legend i { display: inline-block; width: 16px; height: 3px; border-radius: 2px; vertical-align: middle; margin-right: 6px; }
.cmp-legend i.dash { background: repeating-linear-gradient(90deg, var(--c-90a4ae) 0 5px, transparent 5px 8px); }
@keyframes cmpReveal { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); } }
@keyframes cmpFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes cmpPop { from { transform: scale(0); } to { transform: scale(1); } }
@keyframes cmpPing { 0% { transform: scale(1); opacity: .8; } 100% { transform: scale(3.2); opacity: 0; } }
@keyframes cmpShine { 0% { left: -60%; } 55%, 100% { left: 130%; } }
@media (prefers-reduced-motion: reduce) { .cmp-reveal, .cmp-area, .cmp-dot, .cmp-ping, .cmp-stat.now::after { animation: none; } }
`;
